import * as nodeCrypto from "crypto";
import { SigningKey, hexlify, randomBytes } from "ethers";

const ENCRYPTION_ALGORITHM: string = "AES-GCM";
const DERIVATION_ALGORITHM: string = "PBKDF2";
const HASH_ALGORITHM: string = "SHA-256";
const ITERATIONS: number = 100_000;
const SALT_LENGTH: number = 16;
const IV_LENGTH: number = 12;
const TAG_LENGTH: number = 128;
const KEY_LENGTH: number = 256;
const OBFUSCATION_KEY_LENGTH: number = 16;
const SALT: string = "GFALGFALGFALGFAL";
const SALT_BYTES: Uint8Array = stringToUint8Array(SALT);
const ASYMMETRIC_KEY_LENGTH: number = 32;

export const RESCUE_RECIPIENT_KEY_STORAGE_KEY = "gfal-rescue-recipient-key";

export interface AsymmetricKeyPair {
  privateKey: string;
  publicKey: string;
}

interface AsymmetricDTO {
  public: string;
  key: string;
  data: string;
}

export function generateAsymmetricKeyPair(): AsymmetricKeyPair {
  const privateKey = hexlify(randomBytes(32));
  const signingKey = new SigningKey(privateKey);

  return {
    privateKey,
    publicKey: signingKey.compressedPublicKey,
  };
}

function useCrypto(): any {
  if (import.meta.server) return nodeCrypto;
  return crypto;
}

export async function encrypt(input: string, key: string): Promise<string> {
  try {
    if (!input) throw new Error("Input is required.");
    if (!key) throw new Error("Key is required.");
    const saltBytes: Uint8Array = stringToUint8Array(
      generateRandomString(SALT_LENGTH)
    );
    const IVBytes: Uint8Array = generateRandomValues(IV_LENGTH);
    const derivedKey: CryptoKey = await getDeriveCryptoKey(key, saltBytes);
    const encryptedData: ArrayBuffer = await useCrypto().subtle.encrypt(
      { name: ENCRYPTION_ALGORITHM, iv: IVBytes, tagLength: TAG_LENGTH },
      derivedKey,
      stringToUint8Array(input)
    );
    const result: Uint8Array = concat(
      saltBytes,
      IVBytes,
      arrayBufferToUint8Array(encryptedData)
    );
    return uint8ArrayToBase64String(result);
  } catch (error) {
    console.error(`Failed to encrypt input: ${error}`);
    throw new Error("Encryption failed.");
  }
}

export async function decrypt(input: string, key: string): Promise<string> {
  try {
    if (!input) throw new Error("Input is required.");
    if (!key) throw new Error("Key is required.");
    const inputBytes: Uint8Array = base64StringToUint8Array(input);
    const saltBytes: Uint8Array = inputBytes.slice(0, SALT_LENGTH);
    const IVBytes: Uint8Array = inputBytes.slice(
      SALT_LENGTH,
      SALT_LENGTH + IV_LENGTH
    );
    const encryptedData: Uint8Array = inputBytes.slice(SALT_LENGTH + IV_LENGTH);
    const derivedKey: CryptoKey = await getDeriveCryptoKey(key, saltBytes);
    const decryptedData: ArrayBuffer = await useCrypto().subtle.decrypt(
      { name: ENCRYPTION_ALGORITHM, iv: IVBytes, tagLength: TAG_LENGTH },
      derivedKey,
      encryptedData
    );
    return arrayBufferToString(decryptedData);
  } catch (error) {
    console.error(`Failed to decrypt input: ${error}`);
    throw new Error("Decryption failed.");
  }
}

export async function encryptAsymmetric(
  input: string,
  publicKey: string
): Promise<string> {
  try {
    if (!input) throw new Error("Input is required.");
    if (!publicKey) throw new Error("Public key is required.");
    const publicKeyHex: string = withHexPrefix(fromHex(publicKey));
    const key: string = generateRandomString(ASYMMETRIC_KEY_LENGTH);
    const encryptedData: string = await encrypt(input, key);
    const ephemeralPrivateKey = hexlify(randomBytes(32));
    const ephemeralKey = new SigningKey(ephemeralPrivateKey);
    const sharedSecretHex = ephemeralKey.computeSharedSecret(publicKeyHex);
    const sharedSecret: string = await destroy(fromHex(sharedSecretHex));
    const encryptedKey: string = await encrypt(key, sharedSecret);
    const asymmetricDTO: AsymmetricDTO = {
      public: fromHex(ephemeralKey.compressedPublicKey),
      key: encryptedKey,
      data: encryptedData,
    };
    return stringToBase64String(JSON.stringify(asymmetricDTO));
  } catch (error) {
    console.error(`Failed to encrypt asymmetric input: ${error}`);
    throw new Error("Asymmetric encryption failed.");
  }
}

export async function decryptAsymmetric(
  input: string,
  privateKey: string
): Promise<string> {
  try {
    if (!input) throw new Error("Input is required.");
    if (!privateKey) throw new Error("Private key is required.");
    const privateKeyHex: string = withHexPrefix(fromHex(privateKey));
    const inputString: string = base64StringToString(input);
    const asymmetricDTO: AsymmetricDTO = JSON.parse(inputString);
    const privateSigningKey = new SigningKey(privateKeyHex);
    const sharedSecretHex = privateSigningKey.computeSharedSecret(
      withHexPrefix(fromHex(asymmetricDTO.public))
    );
    const sharedSecret: string = await destroy(fromHex(sharedSecretHex));
    const key: string = await decrypt(asymmetricDTO.key, sharedSecret);
    return await decrypt(asymmetricDTO.data, key);
  } catch (error) {
    console.error(`Failed to decrypt asymmetric input: ${error}`);
    throw new Error("Asymmetric decryption failed.");
  }
}

export async function obfuscate(input: string): Promise<string> {
  try {
    if (!input) throw new Error("Input is required.");
    const key: string = generateRandomString(OBFUSCATION_KEY_LENGTH);
    const encrypted: string = await encrypt(input, key);
    return stringToBase64String(key + encrypted);
  } catch (error) {
    console.error(`Failed to obfuscate input: ${error}`);
    throw new Error("Obfuscation failed.");
  }
}

export async function deobfuscate(input: string): Promise<string> {
  try {
    if (!input) throw new Error("Input is required.");
    const plainInput: string = base64StringToString(input);
    const key: string = plainInput.slice(0, OBFUSCATION_KEY_LENGTH);
    const encrypted: string = plainInput.slice(OBFUSCATION_KEY_LENGTH);
    return await decrypt(encrypted, key);
  } catch (error) {
    console.error(`Failed to deobfuscate input: ${error}`);
    throw new Error("Deobfuscation failed.");
  }
}

export async function destroy(input: string): Promise<string> {
  try {
    if (!input) throw new Error("Input is required.");
    const inputBytes: Uint8Array = stringToUint8Array(input);
    const keyMaterial: CryptoKey = await useCrypto().subtle.importKey(
      "raw",
      inputBytes,
      { name: DERIVATION_ALGORITHM },
      false,
      ["deriveBits"]
    );
    const destroyedInput: ArrayBuffer = await useCrypto().subtle.deriveBits(
      {
        name: DERIVATION_ALGORITHM,
        salt: SALT_BYTES,
        iterations: ITERATIONS,
        hash: HASH_ALGORITHM,
      },
      keyMaterial,
      KEY_LENGTH
    );
    return arrayBufferToBase64String(destroyedInput);
  } catch (error) {
    console.error(`Failed to destroy input: ${error}`);
    throw new Error("Destruction failed.");
  }
}

export function generateRandomString(length: number): string {
  const charset: string =
    "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
  const indexes: Uint32Array = useCrypto().getRandomValues(
    new Uint32Array(length)
  );
  let result: string = "";
  for (let i: number = 0; i < length; ++i)
    result += charset[indexes[i] % charset.length];
  return result;
}

async function getDeriveCryptoKey(
  key: string,
  salt: Uint8Array
): Promise<CryptoKey> {
  const keyMaterial: CryptoKey = await useCrypto().subtle.importKey(
    "raw",
    stringToUint8Array(key),
    { name: DERIVATION_ALGORITHM },
    false,
    ["deriveKey"]
  );
  return useCrypto().subtle.deriveKey(
    {
      name: DERIVATION_ALGORITHM,
      salt: salt,
      iterations: ITERATIONS,
      hash: HASH_ALGORITHM,
    },
    keyMaterial,
    { name: ENCRYPTION_ALGORITHM, length: KEY_LENGTH },
    true,
    ["encrypt", "decrypt"]
  );
}

function concat(...arrays: Uint8Array[]): Uint8Array {
  let size: number = 0;
  for (const array of arrays) size += array.length;
  let offset = 0;
  const result = new Uint8Array(size);
  for (const array of arrays) {
    result.set(array, offset);
    offset += array.length;
  }
  return result;
}

function arrayBufferToBase64String(buffer: ArrayBuffer): string {
  return uint8ArrayToBase64String(arrayBufferToUint8Array(buffer));
}

function stringToUint8Array(string: string): Uint8Array {
  return new TextEncoder().encode(string);
}

function uint8ArrayToSting(buffer: Uint8Array): string {
  return new TextDecoder().decode(buffer);
}

function arrayBufferToString(buffer: ArrayBuffer): string {
  return uint8ArrayToSting(arrayBufferToUint8Array(buffer));
}

function arrayBufferToUint8Array(buffer: ArrayBuffer): Uint8Array {
  return new Uint8Array(buffer);
}

function uint8ArrayToBase64String(buffer: Uint8Array): string {
  let binary: string = "";
  for (let i = 0; i < buffer.byteLength; i++)
    binary += String.fromCharCode(buffer[i]);
  return btoa(binary);
}

function base64StringToUint8Array(base64String: string): Uint8Array {
  const binaryString: string = atob(base64String);
  const bytes = new Uint8Array(binaryString.length);
  for (let i = 0; i < binaryString.length; i++)
    bytes[i] = binaryString.charCodeAt(i);
  return bytes;
}

function stringToBase64String(string: string): string {
  return btoa(string);
}

function base64StringToString(base64String: string): string {
  return atob(base64String);
}

function generateRandomValues(length: number): Uint8Array {
  return useCrypto().getRandomValues(new Uint8Array(length));
}

function fromHex(input: string): string {
  if (input.startsWith("0x")) return input.substring(2);
  return input;
}

function withHexPrefix(input: string): string {
  return input.startsWith("0x") ? input : `0x${input}`;
}

export async function parseSecret(secret: string): Promise<AuthPayload | null> {
  if (!secret) return null;
  const deobfuscatedSecret = await deobfuscate(secret);
  const parsedSecret = JSON.parse(deobfuscatedSecret) as AuthPayload;
  return parsedSecret;
}
